import { FC, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Text } from '@/components/Typography';

interface ExpandDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string | null;
  children: ReactNode;
}

export const ExpandDrawer: FC<ExpandDrawerProps> = ({ open, onClose, title, children }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 z-50 flex h-full w-full md:max-w-[90vw] flex-col bg-white transition-transform duration-300 dark:bg-grey-sky-dark ${
          open
            ? 'translate-x-0 shadow-2xl'
            : 'translate-x-full shadow-none pointer-events-none'
        }`}
      >
        <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-peach-1 bg-white px-4 py-3 md:px-6 md:py-4 dark:border-grey-plain-dark dark:bg-grey-sky-dark">
          <Text variant="text-md-bold" className="min-w-0 truncate pr-2 text-grey-black dark:text-grey-main-dark">
            {title ?? 'Details'}
          </Text>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-grey-medium transition-colors hover:bg-peach hover:text-grey-black dark:hover:bg-grey-dark"
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 md:p-6">{children}</div>
      </div>
    </>,
    document.body
  );
};
